import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { DatePipe, DecimalPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { EmailsService } from '../../../core/admin/emails.service';
import {
  EmailLogFilterOption,
  EmailLogRow,
  EmailLogSummary,
} from '../../../core/admin/emails.types';
import { IconComponent } from '../../../shared/icon/icon';
import { StatusChipComponent } from '../../../shared/status-chip/status-chip';
import { DashPipe } from '../../../shared/pipes/dash.pipe';

/** Admin email-log page: paginated outbound notification log + send summary. */
@Component({
  selector: 'app-admin-emails',
  imports: [
    RouterLink,
    DatePipe,
    DecimalPipe,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    IconComponent,
    StatusChipComponent,
    DashPipe,
  ],
  templateUrl: './emails.html',
  styleUrl: './emails.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminEmailsComponent implements OnInit {
  private readonly service = inject(EmailsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly emails = signal<EmailLogRow[]>([]);
  protected readonly summary = signal<EmailLogSummary | null>(null);
  protected readonly categoryOptions = signal<EmailLogFilterOption[]>([]);
  protected readonly statusOptions = signal<EmailLogFilterOption[]>([]);

  protected readonly loading = signal(true);
  protected readonly error = signal(false);

  protected readonly page = signal(1);
  protected readonly pageSize = signal(25);
  protected readonly total = signal(0);
  protected readonly hasNext = signal(false);

  protected readonly categoryFilter = signal<string>('');
  protected readonly statusFilter = signal<string>('');

  // 1-based row range shown in the footer, e.g. "1–25 of 142".
  protected readonly rangeStart = computed(() =>
    this.total() === 0 ? 0 : (this.page() - 1) * this.pageSize() + 1,
  );
  protected readonly rangeEnd = computed(() =>
    Math.min(this.page() * this.pageSize(), this.total()),
  );

  /**
   * Whether the results span more than one page.
   *
   * Style guide: *"Hide the paginator entirely when results fit one page."*
   * A pager over a single row is two dead buttons and a count the table already
   * shows. ⚠️ Not `total() > 0` — that is true of one row.
   *
   * Read off `hasNext` rather than `ceil(total / pageSize)` so it stays correct
   * for an endpoint that reports `has_next` without a reliable total, and the
   * `page() > 1` limb keeps the pager present on the last page, which is the
   * only way back.
   */
  protected readonly showPager = computed(() => this.hasNext() || this.page() > 1);

  ngOnInit(): void {
    this.load();
  }

  protected retry(): void {
    this.load();
  }

  protected onCategoryChange(value: string): void {
    this.categoryFilter.set(value);
    this.page.set(1);
    this.load();
  }

  protected onStatusChange(value: string): void {
    this.statusFilter.set(value);
    this.page.set(1);
    this.load();
  }

  protected prevPage(): void {
    if (this.page() <= 1) return;
    this.page.update((p) => p - 1);
    this.load();
  }

  protected nextPage(): void {
    if (!this.hasNext()) return;
    this.page.update((p) => p + 1);
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.error.set(false);
    this.service
      .list({
        page: this.page(),
        page_size: this.pageSize(),
        category: this.categoryFilter() || undefined,
        status: this.statusFilter() || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.emails.set(res.results);
          this.summary.set(res.summary);
          this.categoryOptions.set(res.filters.categories);
          this.statusOptions.set(res.filters.statuses);
          this.total.set(res.total);
          this.hasNext.set(res.has_next);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }
}
